import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, FormGroup, Input, Label, Row, Table } from "reactstrap";

import { useAuth } from "../../../hooks/auth";
import { useCompanies } from "../../../hooks/companies";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SortHeader from "../../../components/SortHeader";
import TablePagination from "../../../components/TablePagination";
import { exportCsv } from "../../../utils/exportCsv";
import { paginate, sortRows, toggleSort } from "../../../utils/tableHelpers";
import { listarNfseSemNotas } from "../../../services/nfseApi";

import { temPermissao } from "../AuditoriaPedido/regras";
import AssociarNotasModal from "./AssociarNotasModal";
import {
  Aviso,
  EstadoTabela,
  extractError,
  fieldInputStyle,
  fmtBRL,
  fmtData,
  fmtNumber,
  periodoMesAtual,
} from "../_documentosFrete/comum";
import "../_documentosFrete/styles.css";

// NFse sem notas (PO1-3301, épico PO1-3298): NFse que chegaram sem vínculo com
// NF. Clicar na linha abre a associação (por romaneio ou por remetente ×
// período); o backend recalcula o frete e a NFse some da lista.

const PAGE_SIZES = [20, 50, 100];
const PERMISSAO_EDITAR = "hub.nfse.editar";

const EXPORT_COLUMNS = [
  { label: "Número", key: "numero" },
  { label: "Série", key: "serie" },
  { label: "Chave", key: "chave" },
  { label: "Emissão", getValue: (r) => fmtData(r.datEmissao) },
  { label: "Valor", key: "valor" },
  { label: "Transportadora", key: "transportadora" },
  { label: "Tomador", key: "embarcadorRemetente" },
];

const TABLE_COLUMNS = [
  { key: "numero", label: "NFse", sortField: "numero" },
  { key: "chave", label: "Chave" },
  { key: "datEmissao", label: "Emissão", sortField: "datEmissao" },
  { key: "valor", label: "Valor", sortField: "valor", numeric: true },
  { key: "transportadora", label: "Transportadora", sortField: "transportadora" },
  { key: "embarcadorRemetente", label: "Tomador", sortField: "embarcadorRemetente" },
];

const filtrosIniciais = (transportadoraId) => ({
  idTransportadora: transportadoraId ? String(transportadoraId) : "",
  ...periodoMesAtual(),
});

export default function NfseSemNotas() {
  const { user } = useAuth();
  const podeEditar = temPermissao(user, PERMISSAO_EDITAR);
  const { empresaId, transportadoraId, optionsTransportadora } = useCompanies();
  const transportadoras = optionsTransportadora || [];

  const [draft, setDraft] = useState(() => filtrosIniciais(transportadoraId));
  const [filtros, setFiltros] = useState(() => filtrosIniciais(transportadoraId));
  const [busca, setBusca] = useState("");
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [sort, setSort] = useState({ field: "datEmissao", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [aberta, setAberta] = useState(null);
  const [aviso, setAviso] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      const params = { de: filtros.de, ate: filtros.ate };
      if (filtros.idTransportadora) params.idTransportadora = Number(filtros.idTransportadora);
      setItens(await listarNfseSemNotas(params));
    } catch (e) {
      setItens([]);
      setErro(extractError(e, "Não foi possível consultar as NFse sem notas."));
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Troca de embarcador no cabeçalho: os filtros eram do embarcador anterior;
  // volta ao padrão (o novo objeto de filtros dispara a consulta).
  const empresaAnterior = useRef(empresaId);
  useEffect(() => {
    if (empresaAnterior.current === empresaId) return;
    empresaAnterior.current = empresaId;
    const base = filtrosIniciais(transportadoraId);
    setBusca("");
    setAberta(null);
    setAviso(null);
    setDraft(base);
    setFiltros(base);
  }, [empresaId, transportadoraId]);

  const updateDraft = (campo, valor) => setDraft((atual) => ({ ...atual, [campo]: valor }));
  const aplicar = () => {
    const base = periodoMesAtual();
    setFiltros({ ...draft, de: draft.de || base.de, ate: draft.ate || base.ate });
  };
  const limpar = () => {
    const base = filtrosIniciais(transportadoraId);
    setBusca("");
    setDraft(base);
    setFiltros(base);
  };

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return itens;
    return itens.filter((n) =>
      [n.numero, n.chave, n.transportadora, n.embarcadorRemetente].some((v) => String(v || "").toLowerCase().includes(termo))
    );
  }, [busca, itens]);
  const ordenados = useMemo(() => sortRows(filtrados, sort), [filtrados, sort]);
  const pagina = useMemo(() => paginate(ordenados, page, pageSize), [ordenados, page, pageSize]);
  useEffect(() => {
    setPage(1);
  }, [busca, sort, itens]);

  const valorTotal = useMemo(() => filtrados.reduce((acc, n) => acc + (Number(n.valor) || 0), 0), [filtrados]);
  const transportadorasDistintas = useMemo(() => new Set(filtrados.map((n) => n.idTransportadora)).size, [filtrados]);

  const exportar = () => exportCsv({ filename: "nfse-sem-notas", columns: EXPORT_COLUMNS, rows: ordenados });

  return (
    <div className="content docs-frete">
      <PageHero
        eyebrow="Flow · Operação"
        title="NFse sem notas"
        description="NFse que chegaram sem vínculo com nota fiscal. Clique na NFse para associar as notas a que ela se refere, filtrando por romaneio ou por remetente e período."
        actions={
          <>
            <PageHeroRefreshButton onClick={carregar} loading={loading} />
            <PageHeroExportButton onClick={exportar} disabled={!ordenados.length} title="Exportar as NFse da lista" />
          </>
        }
      />

      <FilterBand
        mode="server"
        searchLabel="Buscar"
        searchHint={`Emissão ${fmtData(filtros.de)} a ${fmtData(filtros.ate)}`}
        searchPlaceholder="Filtrar por número, chave, transportadora ou tomador"
        searchValue={busca}
        onSearchChange={setBusca}
        activeFiltersCount={filtros.idTransportadora ? 1 : 0}
        panelTitle="Transportadora e período"
        panelDescription="Data de emissão da NFse. Padrão: mês corrente."
        onApply={aplicar}
        onClear={limpar}
        applyLabel="Buscar"
        busy={loading}
      >
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="nfse-transportadora">Transportadora</Label>
              <Input
                id="nfse-transportadora"
                type="select"
                value={draft.idTransportadora}
                onChange={(e) => updateDraft("idTransportadora", e.target.value)}
                style={fieldInputStyle}
              >
                <option value="">Todas as transportadoras</option>
                {transportadoras.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="nfse-filtro-de">De</Label>
              <Input id="nfse-filtro-de" type="date" value={draft.de} onChange={(e) => updateDraft("de", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="nfse-filtro-ate">Até</Label>
              <Input id="nfse-filtro-ate" type="date" value={draft.ate} onChange={(e) => updateDraft("ate", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
        </Row>
      </FilterBand>

      <MetricCardGroup title="Resumo" description="NFse sem notas no recorte atual.">
        <MetricCard label="NFse sem notas" value={fmtNumber(filtrados.length)} helper="aguardando associação" iconClassName="tim-icons icon-paper" tone="warning" />
        <MetricCard label="Valor" value={fmtBRL(valorTotal)} helper="soma das NFse" iconClassName="tim-icons icon-money-coins" tone="info" />
        <MetricCard label="Transportadoras" value={fmtNumber(transportadorasDistintas)} helper="com NFse pendente" iconClassName="tim-icons icon-delivery-fast" tone="neutral" />
      </MetricCardGroup>

      <ShellCard title="NFse aguardando notas" description={`${fmtNumber(ordenados.length)} NFse · clique na linha para associar as notas`}>
        {aviso ? <Aviso tom={aviso.tom}>{aviso.texto}</Aviso> : null}
        <TableSurface>
          <EstadoTabela loading={loading} erro={erro} vazio={!loading && !ordenados.length} textoCarregando="Consultando NFse..." textoVazio="Nenhuma NFse sem notas no período." />
          {!loading && !erro && ordenados.length ? (
            <>
              <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive hover>
                <thead className="text-primary">
                  <tr>
                    {TABLE_COLUMNS.map((col) => (
                      <th key={col.key} className={col.numeric ? "is-number" : undefined}>
                        {col.sortField ? (
                          <SortHeader label={col.label} field={col.sortField} sort={sort} onSort={(f) => setSort((s) => toggleSort(s, f))} align={col.numeric ? "right" : "left"} />
                        ) : (
                          col.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagina.items.map((n) => (
                    <tr
                      key={n.id}
                      className="is-clicavel"
                      role="button"
                      tabIndex={0}
                      onClick={() => setAberta(n)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setAberta(n);
                        }
                      }}
                      title={podeEditar ? "Associar notas a esta NFse" : "Ver as notas candidatas desta NFse"}
                    >
                      <td>
                        <strong>{n.numero}</strong>
                        <small>Série {n.serie || "-"}</small>
                      </td>
                      <td className="docs-frete__chave">{n.chave || "-"}</td>
                      <td className="docs-frete__nowrap">{fmtData(n.datEmissao)}</td>
                      <td className="is-number">{fmtBRL(n.valor)}</td>
                      <td>
                        <strong>{n.transportadora || "-"}</strong>
                        <small>#{n.idTransportadora}</small>
                      </td>
                      <td>{n.embarcadorRemetente || (n.idEmbarcadorRemetente ? `#${n.idEmbarcadorRemetente}` : "-")}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <TablePagination
                page={pagina.page}
                pageSize={pageSize}
                total={pagina.total}
                pageSizes={PAGE_SIZES}
                itemLabel="NFse"
                onPageChange={setPage}
                onPageSizeChange={(novo) => {
                  setPageSize(novo);
                  setPage(1);
                }}
              />
            </>
          ) : null}
        </TableSurface>
      </ShellCard>

      {aberta ? (
        <AssociarNotasModal
          nfse={aberta}
          empresaId={empresaId}
          podeEditar={podeEditar}
          onClose={(associou) => {
            const numero = aberta.numero;
            setAberta(null);
            if (associou) {
              setAviso({ tom: "ok", texto: `Notas associadas à NFse ${numero}. A lista foi atualizada.` });
              carregar();
            }
          }}
        />
      ) : null}
    </div>
  );
}
